import type {Meta} from '@grafana/faro-core';
import {unknownString} from '@grafana/faro-core';
import {initializeFaro, sdkMeta} from '@grafana/faro-web-sdk';

import type {EnvState} from 'src/action-creators/env';
import {
  resetObservabilityProvider,
  setObservabilityProvider,
} from 'src/utils/observability';
import {FaroObservabilityProvider} from 'src/utils/observability-faro';
import logger from 'src/utils/logger';

/** Faro assumes a DOM + timers; MV3 extension backgrounds are service workers. */
function patchGlobalsForFaroInServiceWorker(): void {
  const g = globalThis as unknown as {
    window?: unknown;
    document?: unknown;
  };
  if (typeof g.window === 'undefined') {
    g.window = globalThis;
  }
  if (typeof g.document === 'undefined') {
    g.document = {
      visibilityState: 'visible',
      addEventListener: () => undefined,
      removeEventListener: () => undefined,
    } as unknown as Document;
  }
}

function extensionContextMeta(
  appUrl: string | null | undefined,
): Partial<Meta> {
  const userAgent =
    typeof navigator !== 'undefined' ? navigator.userAgent : unknownString;
  const language =
    typeof navigator !== 'undefined' && navigator.language
      ? navigator.language
      : unknownString;
  const pageUrl =
    typeof appUrl === 'string' && appUrl.length > 0
      ? appUrl
      : globalThis.location &&
        typeof globalThis.location.href === 'string' &&
        globalThis.location.href.length > 0
      ? globalThis.location.href
      : unknownString;

  return {
    browser: {
      name: unknownString,
      version: unknownString,
      os: unknownString,
      userAgent,
      language,
      mobile: userAgent.includes('Mobi'),
      viewportWidth: '0',
      viewportHeight: '0',
    },
    page: {url: pageUrl},
  };
}

type InitFaroArgs =
  | {
      context: 'webapp';
      env: EnvState;
      agentId: string;
    }
  | {
      context: 'extension';
      release: string;
      appUrl: string | null | undefined;
      agentId: string | null | undefined;
      faroUrl: string;
    };

function getFaroCollectUrl(args: InitFaroArgs): string | undefined {
  if (args.context === 'webapp') {
    const url = args.env.faroUrl;
    return url && url.length > 0 ? url : undefined;
  }
  // extension: faroUrl from iframe SSR env (storage) via UPDATE_FARO_URL bridge
  const url = args.faroUrl;
  return url && url.length > 0 ? url : undefined;
}

function initFaro(args: InitFaroArgs): void {
  try {
    const faroCollectUrl = getFaroCollectUrl(args);
    if (!faroCollectUrl) {
      return;
    }

    const isExtension = args.context === 'extension';

    if (isExtension) {
      patchGlobalsForFaroInServiceWorker();
    }

    const app =
      args.context === 'webapp'
        ? {
            name: 'sense-webapp',
            version: args.env.release,
            environment: args.env.serverRuntimeEnvironment,
          }
        : {
            name: 'sense-messaging-extension',
            version: args.release,
            environment:
              process.env.NODE_ENV === 'production'
                ? 'production'
                : 'development',
          };

    const user = {id: args.agentId ?? unknownString};

    initializeFaro({
      url: faroCollectUrl,
      app,
      instrumentations: [],
      ...(user ? {user} : {}),
      ...(isExtension
        ? {
            // Worker: flush each log immediately (no document visibility / interval batching).
            batching: {sendTimeout: 0, itemLimit: 1},
            dedupe: false,
            metas: [() => extensionContextMeta(args.appUrl), sdkMeta],
          }
        : {}),
    });

    setObservabilityProvider(new FaroObservabilityProvider());
  } catch (error) {
    logger.error('error in initFaro', error);
  }
}

/**
 * Stops web observability on sign-out. Resets the provider only; faro.api may
 * remain until navigation — same pattern as teardownChatExtensionFaro.
 */
export function teardownWebFaro(): void {
  resetObservabilityProvider();
}

export function bootstrapFaro(env: EnvState, agentId: string): void {
  try {
    initFaro({context: 'webapp', env, agentId});
  } catch (error) {
    logger.error('error in bootstrapFaro', error);
  }
}

export function bootstrapFaroExtension(options: {
  release: string;
  appUrl: string | null | undefined;
  agentId: string | null | undefined;
  faroUrl: string;
}): void {
  try {
    initFaro({context: 'extension', ...options});
  } catch (error) {
    logger.error('error in bootstrapFaroExtension', error);
  }
}
